import type { BadgeTone } from '@/shared/components/ui/Badge';
import {
  PROJECT_OBSERVATION_ALLOWED_TRANSITIONS,
  PROJECT_OBSERVATION_STATUS_LABELS,
  PROJECT_OBSERVATION_TYPE_LABELS,
  type HourEntrySummary,
  type ProjectModule,
  type ProjectModuleDelivery,
  type ProjectObservation,
  type ProjectObservationStatus,
  type ProjectObservationType,
} from '@kora/contracts';
import { entryTitle, EVENT_STATUS_LABEL, EVENT_STATUS_TONE } from './hour-entry-log';
import { DELIVERY_DISPLAY_STATUS_LABEL, DELIVERY_DISPLAY_STATUS_TONE, deliveryDisplayStatus } from './delivery-status';

/**
 * Una fila de la bitácora unificada: un evento (hora registrada, de solo
 * lectura acá), un registro manual (hito/incidente/compromiso/atención/
 * observación, creado desde "Agregar registro"), o una entrega de fase — con
 * su estado actual; el líder de proyecto o de equipo la asigna, aprueba,
 * rechaza o revierte desde el menú de acciones (ver `deliveryActions`), y
 * cada uno de esos cambios queda además como un registro `cambio_proyecto`
 * propio (lo escribe el backend). `kind` distingue los tres
 * casos, con el mismo valor que ya trae `ProjectObservation.type` para el
 * segundo. Compartido entre `BitacoraCard` (tabla completa) y
 * `BitacoraMiniCard` (resumen).
 */
export type BitacoraRow =
  | { kind: 'evento'; entry: HourEntrySummary }
  | { kind: ProjectObservationType; observation: ProjectObservation }
  | { kind: 'entrega'; delivery: ProjectModuleDelivery; moduleId: string; moduleName: string };

export function toBitacoraRows(
  events: readonly HourEntrySummary[],
  observations: readonly ProjectObservation[],
  modules: readonly ProjectModule[] = [],
): BitacoraRow[] {
  const eventRows: BitacoraRow[] = events.map((entry) => ({ kind: 'evento', entry }));
  const obsRows: BitacoraRow[] = observations.map((observation) => ({ kind: observation.type, observation }));
  const deliveryRows: BitacoraRow[] = modules.flatMap((module) =>
    module.deliveries.map((delivery) => ({ kind: 'entrega' as const, delivery, moduleId: module.id, moduleName: module.name })),
  );
  return [...eventRows, ...obsRows, ...deliveryRows].sort((a, b) => rowSortKey(b).localeCompare(rowSortKey(a)));
}

export function rowId(row: BitacoraRow): string {
  if (row.kind === 'evento') return row.entry.id;
  if (row.kind === 'entrega') return row.delivery.id;
  return row.observation.id;
}
/** La fila de una entrega se fecha cuando se agregó al proyecto (`createdAt`), no en su fecha agendada — esa va en el detalle. */
export function rowSortKey(row: BitacoraRow): string {
  if (row.kind === 'evento') return row.entry.createdAt;
  if (row.kind === 'entrega') return row.delivery.createdAt;
  return row.observation.createdAt;
}
export function rowFilterDate(row: BitacoraRow): string {
  if (row.kind === 'evento') return row.entry.date;
  if (row.kind === 'entrega') return localIsoDate(row.delivery.createdAt);
  return row.observation.date;
}
export function rowDateDisplay(row: BitacoraRow): string {
  // El registro de activación muestra la fecha del Hito 0 (corregible), no cuándo se registró.
  if (row.kind !== 'evento' && row.kind !== 'entrega' && row.observation.isHito0) {
    return new Date(`${row.observation.date}T00:00:00`).toLocaleDateString('es-CL', { dateStyle: 'short' });
  }
  return new Date(rowSortKey(row)).toLocaleString('es-CL', { dateStyle: 'short', timeStyle: 'short' });
}

/** `YYYY-MM-DD` del instante en la zona horaria local — para comparar contra los filtros Desde/Hasta. */
function localIsoDate(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const OBS_TYPE_TONE: Record<ProjectObservationType, BadgeTone> = {
  hito: 'info',
  incidente: 'danger',
  compromiso: 'warning',
  atencion: 'warning',
  observacion: 'neutral',
  cambio_proyecto: 'neutral',
};

export function rowTypeBadge(row: BitacoraRow): { label: string; tone: BadgeTone } {
  if (row.kind === 'evento') {
    return row.entry.hasBlocker ? { label: 'Incidente', tone: 'danger' } : { label: 'Evento', tone: 'neutral' };
  }
  if (row.kind === 'entrega') return { label: 'Entrega', tone: 'info' };
  // Registro automático sobre una entrega/tarea concreta (asignada, terminada, aprobada, rechazada…).
  if (row.observation.deliveryId) return { label: 'Registro de entrega', tone: 'brand' };
  if (row.observation.taskId) return { label: 'Registro de tarea', tone: 'brand' };
  return { label: PROJECT_OBSERVATION_TYPE_LABELS[row.kind], tone: OBS_TYPE_TONE[row.kind] };
}

export function rowTitle(row: BitacoraRow): string {
  if (row.kind === 'evento') return entryTitle(row.entry);
  if (row.kind === 'entrega') return row.delivery.name ?? `Entrega de fase: ${row.moduleName}`;
  return row.observation.title || row.observation.message;
}

export function rowDescription(row: BitacoraRow): string {
  if (row.kind === 'evento') return row.entry.description;
  if (row.kind === 'entrega') return deliveryTooltip(row);
  return row.observation.message;
}

export function rowTooltip(row: BitacoraRow): string {
  if (row.kind === 'evento') {
    const parts = [row.entry.description || null, row.entry.hasBlocker ? `Bloqueo: ${row.entry.blockerDescription ?? 'sin detalle'}` : null];
    return parts.filter(Boolean).join(' — ');
  }
  if (row.kind === 'entrega') return deliveryTooltip(row);
  return row.observation.title ? row.observation.message : '';
}

function deliveryTooltip(row: { moduleName: string; delivery: ProjectModuleDelivery }): string {
  const { delivery } = row;
  return [
    delivery.description,
    delivery.teamName ? `Equipo ${delivery.teamName}` : null,
    delivery.rejectionReason ? `Rechazada: ${delivery.rejectionReason}` : null,
  ]
    .filter(Boolean)
    .join(' — ');
}

export function rowPhase(row: BitacoraRow): string {
  if (row.kind === 'evento') return row.entry.module?.name ?? '—';
  if (row.kind === 'entrega') return row.moduleName;
  return row.observation.moduleName ?? '—';
}

export function rowResponsible(row: BitacoraRow): string {
  if (row.kind === 'evento') return row.entry.user.fullName;
  // El colaborador asignado es quien responde por la entrega; sin asignar, el equipo a cargo.
  if (row.kind === 'entrega') return row.delivery.assigneeName ?? (row.delivery.teamName ? `${row.delivery.teamName} (sin asignar)` : '—');
  return row.observation.authorName;
}

const OBS_STATUS_TONE: Record<ProjectObservationStatus, BadgeTone> = {
  abierto: 'danger',
  resuelto: 'success',
  pendiente: 'warning',
  cumplido: 'success',
  activo: 'warning',
  rechazado: 'danger',
};

export function rowStatusBadge(row: BitacoraRow): { label: string; tone: BadgeTone } | null {
  if (row.kind === 'evento') return { label: EVENT_STATUS_LABEL[row.entry.status], tone: EVENT_STATUS_TONE[row.entry.status] };
  if (row.kind === 'entrega') {
    const status = deliveryDisplayStatus(row.delivery);
    return { label: DELIVERY_DISPLAY_STATUS_LABEL[status], tone: DELIVERY_DISPLAY_STATUS_TONE[status] };
  }
  if (!row.observation.status) return null;
  return { label: PROJECT_OBSERVATION_STATUS_LABELS[row.observation.status], tone: OBS_STATUS_TONE[row.observation.status] };
}

/** El filtro "Incidentes" junta los dos únicos indicios de "algo anda mal" que existen hoy: un registro tipo Incidente, y una hora con bloqueo reportado. */
export function rowIsIncident(row: BitacoraRow): boolean {
  return row.kind === 'incidente' || (row.kind === 'evento' && row.entry.hasBlocker);
}

/** Filtro "Entregas y tareas": la fila viva de cada entrega y todos los registros de cambios sobre entregas o tareas. */
export function rowIsDeliveryOrTask(row: BitacoraRow): boolean {
  if (row.kind === 'entrega') return true;
  return row.kind !== 'evento' && (row.observation.deliveryId !== null || row.observation.taskId !== null);
}

export type BitacoraRowAction = 'aprobar' | 'rechazar' | 'cerrar' | 'reabrir';

const ACTION_BY_STATUS: Readonly<Partial<Record<ProjectObservationStatus, BitacoraRowAction>>> = {
  cumplido: 'aprobar',
  rechazado: 'rechazar',
  resuelto: 'cerrar',
  abierto: 'reabrir',
};

/**
 * Qué botones de resolución mostrar en la fila (aprobar/rechazar un compromiso, cerrar/reabrir
 * un incidente) — vacío si el actor no puede resolver pendientes (`canResolve`) o si este tipo/estado
 * no tiene una transición definida (ver `PROJECT_OBSERVATION_ALLOWED_TRANSITIONS`).
 */
export function rowResolveActions(row: BitacoraRow): readonly { action: BitacoraRowAction; nextStatus: ProjectObservationStatus }[] {
  if (row.kind !== 'evento' && row.kind !== 'entrega') {
    const { observation } = row;
    if (observation.canResolve && observation.status) {
      const nextStatuses = PROJECT_OBSERVATION_ALLOWED_TRANSITIONS[observation.type]?.[observation.status];
      if (nextStatuses) return nextStatuses.map((nextStatus) => ({ action: ACTION_BY_STATUS[nextStatus] ?? 'aprobar', nextStatus }));
    }
  }
  return [];
}

